// Notice-and-consent demo helpers.
//
// The demo settings panel has a "Notice and consent" toggle:
//   "vx"   -> VX shows its own notice-and-consent screen (default, today's behavior)
//   "site" -> this site shows notice and collects consent itself, and attests to
//             that in the create-journey request so VX can skip its screen.
//
// The request shape below is one builder on purpose: the worker-side spec is
// still being finalized, so expect a rename here rather than in the pages.

export const CONSENT_MODE_VX = "vx";
export const CONSENT_MODE_SITE = "site";

export const NOTICE_REF = "demo-privacy-notice-v1";
export const CONSENT_REF = "demo-terms-v1";

export function consentMode(settings) {
  return settings?.noticeAndConsent === CONSENT_MODE_SITE ? CONSENT_MODE_SITE : CONSENT_MODE_VX;
}

export function isSiteConsent(settings) {
  return consentMode(settings) === CONSENT_MODE_SITE;
}

// ISO 8601 UTC with no milliseconds, e.g. 2026-11-04T16:20:11Z
export function isoNow(date = new Date()) {
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}

// Build the notice_and_consent object for the create-journey body.
// `acceptedAt` is the ISO timestamp captured when the checkbox was ticked;
// it is clamped to now so it can never be in the future.
export function buildNoticeAndConsent(acceptedAt) {
  const now = new Date();
  let at = acceptedAt ? new Date(acceptedAt) : now;
  if (Number.isNaN(at.getTime()) || at > now) at = now;
  const stamp = isoNow(at);
  return {
    notice_and_consent: {
      privacy_attestation: {
        version: "1",
        notice: {
          status: "provided",
          provided_at: stamp,
          provided_by: "company",
          notice_ref: NOTICE_REF,
        },
        consent: {
          status: "obtained",
          obtained_at: stamp,
          obtained_from: "individual",
          consent_ref: CONSENT_REF,
        },
      },
    },
  };
}

// Spread this into a create-journey body: {} when VX shows its own screen,
// the attestation when the site handled notice and consent.
export function consentBodyFields(settings, acceptedAt) {
  return isSiteConsent(settings) ? buildNoticeAndConsent(acceptedAt) : {};
}

export function consentIndicatorLabel(settings) {
  return isSiteConsent(settings)
    ? "VX consent screen: off (attested by site)"
    : "VX consent screen: on";
}

// Placeholder legal copy per demo brand. Clearly dummy text.
export const BRAND_COPY = {
  wine: {
    name: "Online Demo Store",
    terms: [
      "These placeholder Terms of Service govern purchases from the Online Demo Store, a sandbox storefront. No real goods are sold, no payment is taken and no order ships.",
      "By continuing you confirm you meet the minimum age for this store and agree that an age verification check may be performed before an order is accepted.",
    ],
    privacy: [
      "This placeholder Privacy Notice explains that the Online Demo Store collects the contact details you enter at checkout and shares them with Veratad to verify your age.",
      "Verification data is handled by Veratad under its own retention rules; this demo does not keep identity documents.",
    ],
  },
  acme: {
    name: "Demo Online Financial Services",
    terms: [
      "These placeholder Terms of Service describe the Demo Online Financial Services demo account. Nothing here opens a real account or moves real money.",
      "By continuing you agree that Demo Online Financial Services may verify your identity before an account is created.",
    ],
    privacy: [
      "This placeholder Privacy Notice explains that Demo Online Financial Services collects the details on this form and shares them with Veratad for identity verification.",
      "Veratad processes that data under its own privacy terms; the demo retains none of it.",
    ],
  },
  novapay: {
    name: "NovaPay",
    terms: [
      "These placeholder Terms of Service cover the NovaPay demo. No real financial account is created and no funds are held.",
      "By continuing you agree that NovaPay may run an identity check with Veratad before activating your account.",
    ],
    privacy: [
      "This placeholder Privacy Notice explains that NovaPay collects the information you enter during sign-up and passes it to Veratad to confirm who you are.",
      "Identity data is processed by Veratad under its own policy; NovaPay's demo keeps no copy.",
    ],
  },
  socialconnect: {
    name: "SocialConnect",
    terms: [
      "These placeholder Terms of Service apply to the SocialConnect demo community. No real account is created.",
      "By continuing you agree that SocialConnect may check your age with Veratad before your profile goes live.",
    ],
    privacy: [
      "This placeholder Privacy Notice explains that SocialConnect collects your sign-up details and shares them with Veratad for age verification.",
      "Veratad handles that data under its own privacy terms; the demo does not store it.",
    ],
  },
};
